import { CODE_BLOCK, CODE_BLOCKS, isRoomSlug, looksLikeRoomCode } from '@shared/protocol'

const CODE_LENGTH = CODE_BLOCK * CODE_BLOCKS
const NICKNAME_KEY = 'screen-share:nickname'

/**
 * Dois enderecos convivem no mesmo campo: o codigo sorteado da sala do momento
 * (`XXXX-XXXX`) e o endereco da sala fixa (`daily-dos-devs`).
 *
 * Por isso a formatacao so age quando o texto JA E um codigo sorteado completo.
 * Chutar no meio da digitacao corrompia o outro formato: `sala-db` viraria
 * `SALA-DB`, cujo hifen esta no lugar errado — e ai a sala fixa `saladb` nunca
 * seria encontrada. Um endereco de sala fixa nunca pode ser confundido com um
 * codigo completo, porque `isRoomSlug` recusa criar um assim.
 */
export function formatRoomCode(raw: string): string {
    const limpo = raw.trim()
    // A pasted invite link: keep only the room part.
    const fromLink = /\/sala\/([^/?#\s]+)/i.exec(limpo)?.[1] ?? /[?&]code=([^&#\s]+)/i.exec(limpo)?.[1]
    if (fromLink) return formatRoomCode(safeDecode(fromLink))
    const alnum = limpo.replace(/[^a-zA-Z0-9]/g, '')

    if (alnum.length === CODE_LENGTH && looksLikeRoomCode(alnum)) {
        const clean = alnum.toUpperCase()
        return `${clean.slice(0, CODE_BLOCK)}-${clean.slice(CODE_BLOCK)}`
    }

    return limpo.slice(0, 40)
}

function safeDecode(value: string): string {
    try {
        return decodeURIComponent(value)
    } catch {
        return value
    }
}

/** Da pra tentar entrar com o que esta digitado? */
export function isCompleteRoomCode(code: string): boolean {
    const limpo = code.trim()
    const alnum = limpo.replace(/[^a-zA-Z0-9]/g, '')
    if (alnum.length === CODE_LENGTH && looksLikeRoomCode(alnum)) return true
    return isRoomSlug(limpo.toLowerCase())
}

/** Iniciais pro avatar: "Info Stealer" vira "IS", "InfoStealer" vira "IN". */
export function initialsOf(nickname: string): string {
    const words = nickname.trim().split(/\s+/).filter(Boolean)
    if (words.length === 0) return '??'
    if (words.length === 1) return (words[0] ?? '').slice(0, 2).toUpperCase()
    return `${words[0]?.[0] ?? ''}${words[1]?.[0] ?? ''}`.toUpperCase()
}

/** Cor estavel por nickname — mesma pessoa, mesmo avatar em todo mundo. */
export function avatarHue(nickname: string): number {
    let hash = 0
    for (const char of nickname) hash = (hash * 31 + char.charCodeAt(0)) % 360
    return hash
}

export function roomLinkFor(code: string): string {
    if (typeof window === 'undefined') return `/sala/${code}`
    return `${window.location.origin}/sala/${code}`
}

/** Le o codigo da URL (/sala/XXXX-XXXX ou ?code=). */
export function roomCodeFromLocation(pathname: string, search = ''): string {
    const fromPath = /^\/sala\/([^/]+)/i.exec(pathname)?.[1]
    const fromQuery = new URLSearchParams(search).get('code')
    return formatRoomCode(decodeURIComponent(fromPath ?? fromQuery ?? ''))
}

export function loadNickname(): string {
    try {
        return localStorage.getItem(NICKNAME_KEY) ?? ''
    } catch {
        return ''
    }
}

export function saveNickname(nickname: string): void {
    try {
        localStorage.setItem(NICKNAME_KEY, nickname)
    } catch {
        // Modo privado / storage bloqueado: seguir sem lembrar o nickname.
    }
}

const LAST_ROOM_KEY = 'screen-share:last_room'

export function loadLastRoomCode(): string {
    try {
        return formatRoomCode(localStorage.getItem(LAST_ROOM_KEY) ?? '')
    } catch {
        return ''
    }
}

export function saveLastRoomCode(code: string): void {
    try {
        const clean = formatRoomCode(code)
        if (clean) localStorage.setItem(LAST_ROOM_KEY, clean)
    } catch {
        // Nada
    }
}

export function clearLastRoomCode(): void {
    try {
        localStorage.removeItem(LAST_ROOM_KEY)
    } catch {
        // Nada
    }
}
